// /team/programs: Program adopsi, Misi, and in-app Pengingat.
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Theme, themeColors } from '../theme/colors';
import {
  TeamMe,
  ProgramRow,
  ProgramListPayload,
  ProgramInboxItem,
  PlaybookRow,
  TeamError,
  TeamUnauthorizedError,
  listPrograms,
  getProgram,
  createProgram,
  launchProgram,
  completeProgramMission,
  muteProgram,
  remindProgram,
  getProgramProgress,
  listNotifications,
  readNotification,
  listPlaybooks,
  startPlaybook,
  requestProgramHelp,
  listMyHelp,
  HelpRequestRow,
} from './chat/teamService';
import TeamGuide from './TeamGuide';

export interface TeamProgramsProps {
  theme: Theme;
  me: TeamMe;
}

const TeamPrograms: React.FC<TeamProgramsProps> = ({ theme }) => {
  const c = themeColors[theme];
  const navigate = useNavigate();
  const location = useLocation();
  const match = location.pathname.match(/^\/team\/programs\/([^/]+)/);
  const detailUuid = match ? match[1] : null;

  const [payload, setPayload] = useState<ProgramListPayload | null>(null);
  const [detail, setDetail] = useState<ProgramRow | null>(null);
  const [progress, setProgress] = useState<{
    not_started: number; in_progress: number; completed: number;
    members: { user_id: number; email: string | null; status: string }[];
  } | null>(null);
  const [inbox, setInbox] = useState<ProgramInboxItem[]>([]);
  const [playbooks, setPlaybooks] = useState<PlaybookRow[]>([]);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [draftObjective, setDraftObjective] = useState('');
  const [draftMission, setDraftMission] = useState('');
  const [draftPlaybook, setDraftPlaybook] = useState('');
  const [myHelp, setMyHelp] = useState<HelpRequestRow[]>([]);

  const cardStyle: React.CSSProperties = {
    background: c.panel,
    border: `1px solid ${c.line}`,
    borderRadius: '12px',
    padding: '18px',
  };
  const sectionLabelStyle: React.CSSProperties = {
    fontSize: '10px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    color: c.ink3,
    margin: '26px 2px 10px',
  };
  const inputStyle: React.CSSProperties = {
    background: c.panel2,
    border: `1px solid ${c.line2}`,
    borderRadius: '8px',
    padding: '9px 11px',
    fontSize: '13.5px',
    color: c.ink,
    outline: 'none',
    fontFamily: 'inherit',
    minWidth: 0,
    width: '100%',
  };
  const ghostBtnStyle: React.CSSProperties = {
    background: 'none',
    border: `1px solid ${c.line2}`,
    borderRadius: '8px',
    padding: '9px 14px',
    fontSize: '13px',
    fontWeight: 500,
    color: c.ink,
    cursor: 'pointer',
    fontFamily: 'inherit',
    minHeight: '44px',
  };
  const primaryBtnStyle: React.CSSProperties = {
    background: c.btnPrimaryBg,
    color: c.btnPrimaryText,
    border: '1px solid transparent',
    borderRadius: '8px',
    padding: '9px 16px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    minHeight: '44px',
    ['--pa-btn-hover' as any]: c.btnPrimaryHover,
  };

  const describe = (err: unknown) => {
    if (err instanceof TeamUnauthorizedError) {
      window.location.href = '/login';
      return '';
    }
    if (err instanceof TeamError) return err.detail;
    return 'Gagal memuat. Coba lagi.';
  };

  const load = useCallback(async () => {
    setStatus('loading');
    setError('');
    try {
      const [listed, notes, mine] = await Promise.all([
        listPrograms(),
        listNotifications(),
        listMyHelp().catch(() => ({ requests: [] as HelpRequestRow[] })),
      ]);
      setPayload(listed);
      setInbox(notes.notifications);
      setMyHelp(mine.requests || []);
      if (listed.can_create) {
        try {
          const books = await listPlaybooks();
          setPlaybooks(books.playbooks.filter((row) => row.status === 'published'));
        } catch {
          setPlaybooks([]);
        }
      }
      if (detailUuid) {
        const row = await getProgram(detailUuid);
        setDetail(row);
        if (listed.can_create) {
          try {
            const prog = await getProgramProgress(detailUuid);
            setProgress(prog);
          } catch {
            setProgress(null);
          }
        } else {
          setProgress(null);
        }
      } else {
        setDetail(null);
        setProgress(null);
      }
      setStatus('ready');
    } catch (err) {
      const msg = describe(err);
      if (msg) setError(msg);
      setStatus('error');
    }
  }, [detailUuid]);

  useEffect(() => {
    void load();
  }, [load]);

  const canCreate = !!payload?.can_create;
  const rows = payload?.programs ?? [];

  const handleCreate = async () => {
    if (!draftName.trim() || !draftMission.trim() || busy) return;
    setBusy(true);
    setError('');
    try {
      const missions: { name: string; mission_type?: string; playbook_uuid?: string }[] = [
        { name: draftMission.trim() },
      ];
      if (draftPlaybook) {
        missions[0] = {
          name: draftMission.trim(),
          mission_type: 'playbook',
          playbook_uuid: draftPlaybook,
        };
        missions.push({ name: 'Tandai pekerjaan selesai', mission_type: 'custom' });
      }
      const created = await createProgram({
        name: draftName.trim(),
        objective: draftObjective.trim() || undefined,
        missions,
      });
      setNotice('Draft Program tersimpan. Luncurkan supaya anggota menerima Pengingat.');
      navigate(`/team/programs/${created.uuid}`);
    } catch (err) {
      const msg = describe(err);
      if (msg) setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleLaunch = async (uuid: string) => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await launchProgram(uuid);
      setNotice('Program diluncurkan. Anggota mendapat Pengingat di aplikasi.');
      await load();
    } catch (err) {
      const msg = describe(err);
      if (msg) setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleComplete = async (uuid: string, missionId: number) => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const row = await completeProgramMission(uuid, missionId);
      setDetail(row);
      setNotice('Misi ditandai selesai.');
    } catch (err) {
      const msg = describe(err);
      if (msg) setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleHelp = async (uuid: string, missionId: number) => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await requestProgramHelp(uuid, { topic: 'misi', mission_id: missionId });
      setNotice('Permintaan terkirim ke Champion AI. Isi chat tidak ikut terkirim.');
      await load();
    } catch (err) {
      const msg = describe(err);
      if (msg) setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleStartMission = async (_row: ProgramRow, mission: ProgramRow['missions'][0]) => {
    if (mission.mission_type === 'playbook' && mission.playbook_id) {
      const listed = await listPlaybooks();
      const uuid = listed.playbooks.find((p) => p.id === mission.playbook_id)?.uuid;
      if (uuid) {
        const started = await startPlaybook(uuid);
        navigate(`/c/${started.conversation_uuid}`);
        return;
      }
    }
    navigate('/team/playbooks');
  };

  if (status === 'loading' && !payload) {
    return <div style={{ fontSize: '13px', color: c.ink2 }}>Memuat Program adopsi…</div>;
  }

  if (detailUuid && detail) {
    return (
      <>
        <button type="button" onClick={() => navigate('/team/programs')} className="pa-ghost" style={{ ...ghostBtnStyle, marginBottom: '8px' }}>
          Semua Program
        </button>
        <div style={sectionLabelStyle}>Program adopsi</div>
        {error && <div style={{ fontSize: '12.5px', color: theme === 'light' ? '#DC2626' : '#F87171', marginBottom: '10px' }}>{error}</div>}
        {notice && <div style={{ fontSize: '12.5px', color: c.ink2, marginBottom: '10px' }}>{notice}</div>}
        <div style={cardStyle}>
          <div style={{ fontSize: '18px', fontWeight: 650, color: c.ink }}>{detail.name}</div>
          {detail.objective && (
            <div style={{ fontSize: '13.5px', color: c.ink2, marginTop: '8px', lineHeight: 1.65 }}>{detail.objective}</div>
          )}
          <div style={{ fontSize: '12px', color: c.ink3, marginTop: '10px' }}>
            {detail.status === 'draft' ? 'Draft' : detail.status === 'active' ? 'Aktif' : detail.status}
            {` · ${detail.completed_count} dari ${detail.participant_count} selesai`}
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '14px' }}>
            {canCreate && detail.status === 'draft' && (
              <button type="button" onClick={() => handleLaunch(detail.uuid)} disabled={busy} className="pa-primary" style={{ ...primaryBtnStyle, opacity: busy ? 0.45 : 1 }}>
                Luncurkan
              </button>
            )}
            {canCreate && detail.status === 'active' && (
              <button type="button" onClick={() => remindProgram(detail.uuid).then(() => setNotice('Pengingat dikirim ke yang belum selesai.')).catch((err) => setError(describe(err)))} className="pa-ghost" style={ghostBtnStyle}>
                Kirim Pengingat
              </button>
            )}
            {detail.my_status && detail.my_status !== 'completed' && !detail.muted && (
              <button type="button" onClick={() => muteProgram(detail.uuid).then(() => { setNotice('Pengingat Program ini dimatikan.'); void load(); })} className="pa-ghost" style={ghostBtnStyle}>
                Matikan Pengingat
              </button>
            )}
          </div>
        </div>
        <div style={sectionLabelStyle}>Misi</div>
        {detail.missions.map((mission) => (
          <div key={mission.id} style={{ ...cardStyle, marginTop: '10px' }}>
            <div style={{ fontSize: '15px', fontWeight: 600, color: c.ink }}>{mission.name}</div>
            {mission.description && (
              <div style={{ fontSize: '12.5px', color: c.ink2, marginTop: '4px', lineHeight: 1.6 }}>{mission.description}</div>
            )}
            <div style={{ fontSize: '12px', color: c.ink3, marginTop: '8px' }}>
              {mission.my_status === 'completed'
                ? 'Selesai'
                : mission.my_status === 'needs_help'
                  ? 'Minta bantuan'
                  : 'Belum selesai'}
              {mission.playbook_name ? ` · Playbook ${mission.playbook_name}` : ''}
            </div>
            {(() => {
              const help = myHelp.find((row) => (
                row.program_uuid === detail.uuid
                && (row.mission_id == null || row.mission_id === mission.id)
              ));
              if (!help?.reply_text) return null;
              return (
                <div style={{ fontSize: '13px', color: c.ink, marginTop: '10px', lineHeight: 1.6 }}>
                  Balasan Champion: {help.reply_text}
                </div>
              );
            })()}
            {detail.status === 'active' && mission.my_status !== 'completed' && (
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px' }}>
                {mission.mission_type === 'playbook' && (
                  <button type="button" onClick={() => handleStartMission(detail, mission)} className="pa-primary" style={primaryBtnStyle}>
                    Mulai di Power AI
                  </button>
                )}
                <button type="button" onClick={() => handleComplete(detail.uuid, mission.id)} disabled={busy} className="pa-ghost" style={ghostBtnStyle}>
                  Tandai selesai
                </button>
                <button type="button" onClick={() => handleHelp(detail.uuid, mission.id)} disabled={busy} className="pa-ghost" style={ghostBtnStyle}>
                  Saya butuh bantuan
                </button>
              </div>
            )}
          </div>
        ))}
        {progress && (
          <>
            <div style={sectionLabelStyle}>Progres</div>
            <div style={cardStyle}>
              <div style={{ fontSize: '13px', color: c.ink2, lineHeight: 1.7 }}>
                {progress.completed} selesai · {progress.in_progress} sedang jalan · {progress.not_started} belum mulai
              </div>
            </div>
          </>
        )}
      </>
    );
  }

  return (
    <>
      <div style={sectionLabelStyle}>Program adopsi</div>
      <div style={cardStyle}>
        <TeamGuide
          theme={theme}
          lead={[
            'Program adopsi adalah PR ke anggota: kerjakan Misi ini, minggu ini. SOP-nya tetap di Playbook. Yang ada di sini pengingat dan centangan.',
            'Pengingat muncul di aplikasi, paling banyak sekali per 24 jam. Kalau Misi tertaut Playbook, menandai Playbook selesai ikut menandai Misi itu.',
          ]}
          exampleName="Onboarding Power AI"
          exampleBody={[
            'Sari baru gabung. Kamu luncurkan Program: Misi 1 jalankan Playbook Laporan kunjungan, Misi 2 tandai pekerjaan selesai.',
            'Sari melihat kartu Misi kamu di chat baru, atau Pengingat di halaman ini. Dia tekan Mulai di Power AI, ngerjain laporan, lalu Tandai selesai. Kamu lihat progresnya di sini, tanpa membuka isi chat Sari.',
          ]}
          columns={
            canCreate
              ? [
                  {
                    title: 'Kamu',
                    steps: [
                      'Buat Program, tautkan Playbook kalau sudah ada. Simpan draft.',
                      'Tekan Luncurkan. Anggota mendapat Pengingat dan kartu Misi kamu di chat baru.',
                      'Kirim Pengingat ke yang belum selesai. Reminder berhenti sendiri begitu Misi beres.',
                    ],
                  },
                  {
                    title: 'Anggota, misalnya Sari',
                    steps: [
                      'Buka Pengingat di halaman ini, atau kartu Misi kamu di chat baru.',
                      'Kalau Misi tertaut Playbook, tekan Mulai di Power AI. Ngobrol sampai hasilnya beres.',
                      'Tandai selesai, atau tekan Saya butuh bantuan kalau macet. Isi chat tidak ikut terkirim.',
                    ],
                  },
                ]
              : [
                  {
                    title: 'Cara pakai',
                    steps: [
                      'Buka Pengingat di halaman ini, atau kartu Misi kamu di chat baru.',
                      'Kerjakan Misi. Kalau tertaut Playbook, tekan Mulai di Power AI.',
                      'Tandai selesai. Playbook yang sudah ditandai selesai menandai Misi terkait sendiri.',
                    ],
                  },
                ]
          }
          related={[
            { name: 'Playbook', body: 'SOP-nya. Program hanya menyuruh Sari menjalankannya.' },
            { name: 'Champion AI', body: 'Kalau Sari macet, dia tekan Saya butuh bantuan. Isi chat tidak ikut.' },
            { name: 'Adopsi', body: 'Papan skor pemilik setelah Program jalan. Isi chat tidak tampil.' },
          ]}
          faqs={[
            {
              q: 'Kenapa perlu Program kalau Playbook sudah ada?',
              a: 'Playbook adalah SOP-nya. Program adalah PR-nya: "Sari, minggu ini kerjakan Laporan kunjungan." Tanpa Program, Playbook hanya perpustakaan. Orang yang belum mulai tidak dijemput.',
            },
          ]}
        />
      </div>
      {error && <div style={{ fontSize: '12.5px', color: theme === 'light' ? '#DC2626' : '#F87171', margin: '10px 2px 0' }}>{error}</div>}
      {notice && <div style={{ fontSize: '12.5px', color: c.ink2, margin: '10px 2px 0' }}>{notice}</div>}
      {inbox.length > 0 && (
        <>
          <div style={sectionLabelStyle}>Pengingat</div>
          {inbox.slice(0, 5).map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => {
                void readNotification(item.id);
                if (item.template_key === 'help_requested' || item.template_key === 'adoption_digest') {
                  navigate('/team/champion');
                  return;
                }
                if (item.program_uuid) navigate(`/team/programs/${item.program_uuid}`);
              }}
              style={{
                ...cardStyle,
                marginTop: '8px',
                width: '100%',
                textAlign: 'left',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              <div style={{ fontSize: '13.5px', fontWeight: 600, color: c.ink }}>
                {item.template_key === 'program_remind'
                  ? 'Pengingat Misi'
                  : item.template_key === 'help_reply'
                    ? 'Champion sudah membalas'
                    : item.template_key === 'help_requested'
                      ? 'Ada yang minta bantuan'
                      : item.template_key === 'adoption_digest'
                        ? 'Digest adopsi minggu ini'
                        : 'Program baru'}
              </div>
              <div style={{ fontSize: '12.5px', color: c.ink2, marginTop: '4px' }}>
                {item.template_key === 'adoption_digest'
                  ? 'Ringkasan adopsi minggu ini'
                  : item.template_key === 'help_requested'
                    ? 'Buka antrian Champion AI'
                    : item.program_name || 'Program adopsi'}
              </div>
            </button>
          ))}
        </>
      )}
      {canCreate && (
        <div style={{ ...cardStyle, marginTop: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: c.ink }}>Buat Program</div>
            <button
              type="button"
              className="pa-ghost"
              style={ghostBtnStyle}
              onClick={() => {
                setDraftName('Onboarding Power AI');
                setDraftObjective('Setiap anggota menyelesaikan satu pekerjaan nyata di minggu pertama.');
                setDraftMission('Jalankan Playbook Laporan kunjungan');
                if (playbooks[0]) setDraftPlaybook(playbooks[0].uuid);
              }}
            >
              Pakai contoh
            </button>
          </div>
          <div style={{ display: 'grid', gap: '10px' }}>
            <label style={{ display: 'grid', gap: '6px' }}>
              <span style={{ fontSize: '12px', color: c.ink2 }}>Nama Program</span>
              <input
                className="pa-field"
                placeholder="Onboarding Power AI"
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                maxLength={80}
                style={inputStyle}
              />
            </label>
            <label style={{ display: 'grid', gap: '6px' }}>
              <span style={{ fontSize: '12px', color: c.ink2 }}>Tujuan</span>
              <input
                className="pa-field"
                placeholder="Setiap anggota menyelesaikan satu pekerjaan nyata di minggu pertama"
                value={draftObjective}
                onChange={(e) => setDraftObjective(e.target.value)}
                maxLength={280}
                style={inputStyle}
              />
            </label>
            <label style={{ display: 'grid', gap: '6px' }}>
              <span style={{ fontSize: '12px', color: c.ink2 }}>Misi pertama</span>
              <input
                className="pa-field"
                placeholder="Jalankan Playbook Laporan kunjungan"
                value={draftMission}
                onChange={(e) => setDraftMission(e.target.value)}
                maxLength={80}
                style={inputStyle}
              />
            </label>
            {playbooks.length > 0 && (
              <label style={{ display: 'grid', gap: '6px' }}>
                <span style={{ fontSize: '12px', color: c.ink2 }}>Playbook terkait</span>
                <select className="pa-field" value={draftPlaybook} onChange={(e) => setDraftPlaybook(e.target.value)} style={inputStyle}>
                  <option value="">Tanpa Playbook</option>
                  {playbooks.map((row) => (
                    <option key={row.uuid} value={row.uuid}>{row.name}</option>
                  ))}
                </select>
              </label>
            )}
            <button
              type="button"
              onClick={handleCreate}
              disabled={busy || !draftName.trim() || !draftMission.trim()}
              className="pa-primary"
              style={{ ...primaryBtnStyle, opacity: busy || !draftName.trim() || !draftMission.trim() ? 0.45 : 1, justifySelf: 'start' }}
            >
              Simpan draft
            </button>
          </div>
        </div>
      )}
      {rows.length === 0 ? (
        <div style={{ ...cardStyle, marginTop: '12px', fontSize: '13px', color: c.ink2, lineHeight: 1.65 }}>
          {canCreate
            ? 'Belum ada Program. Isi form di atas, atau tekan Pakai contoh, lalu simpan dan luncurkan.'
            : 'Belum ada Program adopsi.'}
        </div>
      ) : (
        rows.map((row) => (
          <div key={row.uuid} style={{ ...cardStyle, marginTop: '10px' }}>
            <div style={{ fontSize: '15px', fontWeight: 600, color: c.ink }}>{row.name}</div>
            {row.objective && <div style={{ fontSize: '12.5px', color: c.ink2, marginTop: '4px' }}>{row.objective}</div>}
            <div style={{ fontSize: '12px', color: c.ink3, marginTop: '8px' }}>
              {row.status === 'draft' ? 'Draft' : row.status === 'active' ? 'Aktif' : row.status}
              {` · ${row.completed_count} dari ${row.participant_count} selesai`}
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px' }}>
              <button type="button" onClick={() => navigate(`/team/programs/${row.uuid}`)} className="pa-ghost" style={ghostBtnStyle}>Lihat</button>
              {canCreate && row.status === 'draft' && (
                <button type="button" onClick={() => handleLaunch(row.uuid)} disabled={busy} className="pa-primary" style={primaryBtnStyle}>Luncurkan</button>
              )}
            </div>
          </div>
        ))
      )}
    </>
  );
};

export default TeamPrograms;
